/**
 * circuitGeometry — pure projection + matching helpers for the live track map.
 * -------------------------------------------------------------------------
 * Zero I/O by design: every function here is a deterministic transform, so the
 * whole module is unit-testable offline (see scripts/test-data-integrity.mjs).
 * The two things that fetch — the circuit outline (jsDelivr) and OpenF1
 * telemetry — live in circuitService.js / openF1LiveClient.js and hand their
 * raw arrays to these functions.
 *
 * TWO COORDINATE SYSTEMS, NEVER OVERLAID
 * --------------------------------------
 *   1. Circuit SHAPE from bacinger GeoJSON — WGS84 lon/lat. Used for the
 *      static backdrop shown while live positions are unavailable. projectLonLat.
 *   2. Live CAR positions from OpenF1 /location — a local, per-circuit metric
 *      frame (arbitrary origin/rotation, ~10 cm units). The live outline is
 *      traced from the SAME telemetry so cars sit exactly on it. fitPoints +
 *      projectXY.
 * They are rendered in different states, so their frames never need to align.
 */

/** Diacritic-insensitive, lowercased, trimmed — for venue-name matching. */
function norm(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * matchCircuit — resolve an OpenF1 session onto a bacinger circuit entry.
 *
 * OpenF1 gives `location` (host city, e.g. "Budapest"), `circuit_short_name`
 * (e.g. "Hungaroring") and `country_name`. The city is the most reliable key —
 * it is unique per venue on the calendar — so we try it first, then the circuit
 * name, then a loose contains-match either direction.
 *
 * @returns {{id:string,name:string,location:string}|null}
 */
export function matchCircuit(session, catalog) {
  if (!session || !Array.isArray(catalog)) return null;
  const city = norm(session.location);
  const shortName = norm(session.circuit_short_name);

  // 1. Exact host-city match.
  if (city) {
    const hit = catalog.find((c) => norm(c.location) === city);
    if (hit) return { id: hit.id, name: hit.name, location: hit.location };
  }
  // 2. Circuit-name match (exact, then contains either direction).
  if (shortName) {
    const exact = catalog.find((c) => norm(c.name) === shortName);
    if (exact) return { id: exact.id, name: exact.name, location: exact.location };
    const loose = catalog.find((c) => {
      const n = norm(c.name);
      return n.includes(shortName) || shortName.includes(n);
    });
    if (loose) return { id: loose.id, name: loose.name, location: loose.location };
  }
  // 3. Loose host-city contains (handles "Spa" vs "Spa Francorchamps" etc.).
  if (city) {
    const loose = catalog.find((c) => {
      const l = norm(c.location);
      return l.includes(city) || city.includes(l);
    });
    if (loose) return { id: loose.id, name: loose.name, location: loose.location };
  }
  return null;
}

/**
 * fitPoints — map an array of [x,y] world points into a fixed viewBox,
 * preserving aspect ratio and centring the shape with padding.
 *
 * Returns the transform plus a `to(x,y)` mapper so callers can place both the
 * traced outline and the live dots through the SAME frame (co-registration).
 *
 * @param {Array<[number,number]>} pts
 * @param {object} [opts]
 * @param {number} [opts.width=1000] @param {number} [opts.height=1000]
 * @param {number} [opts.padding=48]
 * @param {boolean} [opts.flipY=true]  screen Y grows downward; flip so "up" is up
 */
export function fitPoints(pts, { width = 1000, height = 1000, padding = 48, flipY = true } = {}) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    const x = p[0];
    const y = p[1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) {
    // No usable points — return an identity-ish frame so callers don't crash.
    const to = (x, y) => [x, y];
    return { to, width, height, scale: 1, minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  const spanX = Math.max(maxX - minX, 1e-9);
  const spanY = Math.max(maxY - minY, 1e-9);
  const innerW = width - 2 * padding;
  const innerH = height - 2 * padding;
  const scale = Math.min(innerW / spanX, innerH / spanY);
  const offsetX = padding + (innerW - spanX * scale) / 2;
  const offsetY = padding + (innerH - spanY * scale) / 2;
  const to = (x, y) => [
    offsetX + (x - minX) * scale,
    flipY ? offsetY + (maxY - y) * scale : offsetY + (y - minY) * scale,
  ];
  return { to, width, height, scale, minX, maxX, minY, maxY };
}

/** Round to 2 dp for compact, stable path strings. */
const r2 = (n) => Math.round(n * 100) / 100;

/** Build an SVG path `d` from [x,y] points already mapped through a frame. */
function pathFromScreen(screenPts, { close = false } = {}) {
  if (screenPts.length === 0) return '';
  let d = '';
  for (let i = 0; i < screenPts.length; i++) {
    const [x, y] = screenPts[i];
    d += `${i === 0 ? 'M' : 'L'}${r2(x)} ${r2(y)}`;
    if (i < screenPts.length - 1) d += ' ';
  }
  if (close) d += ' Z';
  return d;
}

/**
 * projectLonLat — bacinger GeoJSON [lon,lat] ring → SVG path + viewBox.
 *
 * Longitude degrees shrink with latitude, so we pre-scale X by cos(meanLat)
 * before fitting; otherwise every circuit north/south of the equator renders
 * horizontally stretched.
 *
 * @param {Array<[number,number]>} coords  lon/lat pairs (GeoJSON order)
 * @returns {{d:string,viewBox:string,width:number,height:number}}
 */
export function projectLonLat(coords, opts = {}) {
  const clean = (coords ?? []).filter(
    (c) => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1])
  );
  if (clean.length === 0) return { d: '', viewBox: '0 0 1000 1000', width: 1000, height: 1000 };

  const meanLat = clean.reduce((s, c) => s + c[1], 0) / clean.length;
  const kx = Math.cos((meanLat * Math.PI) / 180) || 1;
  const world = clean.map(([lon, lat]) => [lon * kx, lat]);
  const frame = fitPoints(world, opts);
  const screen = world.map(([x, y]) => frame.to(x, y));
  // GeoJSON rings usually repeat the first point last; close explicitly anyway.
  const closed =
    clean.length > 2 && clean[0][0] === clean[clean.length - 1][0] && clean[0][1] === clean[clean.length - 1][1];
  return {
    d: pathFromScreen(screen, { close: !closed }),
    viewBox: `0 0 ${frame.width} ${frame.height}`,
    width: frame.width,
    height: frame.height,
  };
}

/**
 * projectXY — OpenF1 local [x,y] telemetry points → SVG path + the frame used,
 * so the caller can place live dots through `frame.to(x,y)`.
 *
 * @param {Array<[number,number]>} points
 * @returns {{d:string,viewBox:string,frame:object}}
 */
export function projectXY(points, opts = {}) {
  const clean = (points ?? []).filter(
    (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])
  );
  const frame = fitPoints(clean, opts);
  const screen = clean.map(([x, y]) => frame.to(x, y));
  return {
    d: pathFromScreen(screen, { close: false }),
    viewBox: `0 0 ${frame.width} ${frame.height}`,
    frame,
  };
}

/**
 * latestPerDriver — collapse a flat list of OpenF1 /location rows to the most
 * recent sample per driver. Rows: { driver_number, x, y, date }.
 *
 * @returns {Map<number,{x:number,y:number,date:string}>}
 */
export function latestPerDriver(rows) {
  const latest = new Map();
  for (const row of rows ?? []) {
    const num = row?.driver_number;
    if (num == null) continue;
    // Skip the (0,0) rows OpenF1 emits before a car's transponder is live.
    if (!Number.isFinite(row.x) || !Number.isFinite(row.y)) continue;
    if (row.x === 0 && row.y === 0) continue;
    const prev = latest.get(num);
    if (!prev || new Date(row.date) > new Date(prev.date)) {
      latest.set(num, { x: row.x, y: row.y, date: row.date });
    }
  }
  return latest;
}
